import { useNavigate } from "@tanstack/react-router";
import { Avatar, Button, Layout, Space } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuthUser } from "../../stores/UseAuth";
import UserProfileModal from "../../components/common/UserProfileModal";
import styled from "styled-components";

const { Content } = Layout;

export function MainLayout({ children, pageProps }: any) {
  const signOut = useAuthUser((state) => state.signOut);
  const navigate = useNavigate();
  const user = useAuthUser((state) => state.user);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [profileModalVisible, setProfileModalVisible] =
    useState<boolean>(false);
  const [scrollProgress, setScrollProgress] = useState<number>(0);
  const [showBackTop, setShowBackTop] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = () => {
    setProfileModalVisible(false);
    signOut();
    navigate({ to: "/login" });
  };

  const handleUserClick = () => {
    if (user) {
      setProfileModalVisible(true);
    }
  };

  // Kiểm tra kích thước màn hình để xác định thiết bị mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      const scrollTop = el.scrollTop;
      const docHeight = el.scrollHeight - el.clientHeight;
      const pct = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
      setScrollProgress(pct);
      setShowBackTop(scrollTop > 300);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true } as any);
    return () => el.removeEventListener("scroll", onScroll as any);
  }, [scrollContainerRef.current]);

  const circle = useMemo(() => {
    const r = 20;
    const c = 2 * Math.PI * r;
    const dashoffset = c * (1 - scrollProgress);
    return { r, c, dashoffset };
  }, [scrollProgress]);

  const handleBackToTop = () => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const hideHeader = !!pageProps?.hideHeader;
  return (
    <Container>
      <Layout style={{ backgroundColor: "#f6f7f9" }}>
        <Layout
          style={{
            backgroundColor: "#f6f7f9",
          }}
        >
          {!hideHeader && (
            <div
              style={{
                backgroundColor: "#fff",
                padding: isMobile ? "0px" : "0 24px",
                height: isMobile ? 60 : 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                borderBottom: "1px solid #f0f0f0",
                position: "sticky",
                top: 0,
                zIndex: 99,
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 1024, // ~max-w-4xl
                  margin: "0 auto",
                  padding: isMobile ? "0 16px" : "0 24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <a href="/" style={{ display: "inline-flex" }}>
                    <img
                      src="/images/logo-bg-white.jpg"
                      alt="Logo"
                      style={{ height: isMobile ? 28 : 32, width: "auto" }}
                    />
                  </a>
                  <Button
                    type="text"
                    onClick={handleUserClick}
                    style={{
                      padding: "4px 8px",
                      height: "auto",
                      cursor: user ? "pointer" : "default",
                    }}
                  >
                    <Space>
                      {user ? (
                        <>
                          <Avatar
                            src={user.socialite_avatar || user.avatar}
                            size={isMobile ? "small" : "default"}
                            style={{ backgroundColor: "#0573ff" }}
                          >
                            {!(user.socialite_avatar || user.avatar) &&
                              user.full_name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          {!isMobile && (
                            <span style={{ fontWeight: 500, fontSize: 14 }}>
                              {user.full_name}
                            </span>
                          )}
                        </>
                      ) : (
                        <Avatar
                          size={isMobile ? "small" : "default"}
                          style={{ backgroundColor: "#0573ff" }}
                        />
                      )}
                    </Space>
                  </Button>
                </div>
              </div>

              {user && (
                <UserProfileModal
                  user={user}
                  visible={profileModalVisible}
                  onClose={() => setProfileModalVisible(false)}
                  onLogout={handleLogout}
                />
              )}
            </div>
          )}
          <Content
            style={{
              overflow: "hidden",
              position: "relative",
              height: hideHeader
                ? "100svh"
                : isMobile
                  ? "calc(100svh - 120px)"
                  : "calc(100svh - 64px)",
              padding: hideHeader ? 0 : 16,
            }}
          >
            <div
              ref={scrollContainerRef}
              style={{
                height: "100%",
                overflow: hideHeader ? "hidden" : "auto",
              }}
            >
              {children}
            </div>
            {showBackTop && (
              <button
                onClick={handleBackToTop}
                aria-label="Back to top"
                style={{
                  position: "fixed",
                  right: 9,
                  bottom: 24,
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 100,
                }}
              >
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  style={{ position: "absolute" }}
                >
                  <circle
                    cx="28"
                    cy="28"
                    r={circle.r}
                    stroke="#e5e7eb"
                    strokeWidth={4}
                    fill="none"
                  />
                  <circle
                    cx="28"
                    cy="28"
                    r={circle.r}
                    stroke="#1677ff"
                    strokeWidth={4}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circle.c}
                    strokeDashoffset={circle.dashoffset}
                    transform="rotate(-90 28 28)"
                  />
                </svg>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1677ff"
                  strokeWidth="2.5"
                >
                  <path d="M12 19V6" strokeLinecap="round" />
                  <path
                    d="M6 12l6-6 6 6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </Content>
        </Layout>
      </Layout>
    </Container>
  );
}

const Container = styled.div`
  .ant-layout-content {
    padding: 0px !important;
  }
`;

export default MainLayout;
